import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Clock, Flame, CheckCircle2, Loader2, ArrowRight, AlertTriangle,
  Share2, Copy, Twitter, Instagram, Youtube, Music2,
} from "lucide-react";

/*  THE 60-SECOND CHALLENGE — public, bookmarkable version of the Interrogation.
    Same case data, same scoring, same share card. The difference: this page
    is URL-direct, has social captions, and lives at /challenge for linking
    from TikTok bios, DMs, and the verdict card itself.
*/

const PLATFORM_META = [
  { key: "tiktok",    label: "TikTok",    Icon: Music2 },
  { key: "instagram", label: "Instagram", Icon: Instagram },
  { key: "x",         label: "X",         Icon: Twitter },
  { key: "youtube",   label: "YouTube",   Icon: Youtube },
  { key: "snapchat",  label: "Snapchat",  Icon: Share2 },
];

const Challenge = () => {
  const [params] = useSearchParams();
  const inboundShare = params.get("share") || "";
  const fromTag = params.get("from") || "";

  const [stage, setStage]       = useState("intro");    // intro | playing | verdict | inbound
  const [caseData, setCaseData] = useState(null);
  const [inboundCard, setInboundCard] = useState(null);
  const [picked, setPicked]     = useState("");
  const [left, setLeft]         = useState(60);
  const [verdict, setVerdict]   = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const timerRef = useRef(null);

  // If we deep-linked from someone's share, show their verdict first then let visitor play.
  useEffect(() => {
    if (!inboundShare) return;
    api.get(`/challenge/share/${encodeURIComponent(inboundShare)}`)
      .then((r) => {
        setInboundCard(r.data);
        setStage("inbound");
      })
      .catch(() => {});
  }, [inboundShare]);

  // Load today's case on mount (even if we show inbound card first)
  useEffect(() => {
    api.get("/interrogation/case")
      .then((r) => setCaseData(r.data.case))
      .catch(() => toast.error("Comms stuttered. Try reloading."));
  }, []);

  // Countdown
  useEffect(() => {
    if (stage !== "playing") return;
    timerRef.current = setInterval(() => {
      setLeft((n) => {
        if (n <= 1) {
          clearInterval(timerRef.current);
          submit(0);
          return 0;
        }
        return n - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  const start = () => {
    setLeft(60);
    setStage("playing");
  };

  const submit = async (overrideLeft = null) => {
    if (submitting) return;
    setSubmitting(true);
    clearInterval(timerRef.current);
    try {
      const { data } = await api.post("/interrogation/verdict", {
        guess_key:   picked || "none",
        time_left_s: overrideLeft ?? left,
      });
      setVerdict(data);
      setStage("verdict");
    } catch {
      toast.error("Couldn't lock in that verdict. Try once more.");
    } finally {
      setSubmitting(false);
    }
  };

  const shareNative = async () => {
    if (!verdict) return;
    const title = verdict.correct
      ? `Cracked today's WasntMe case in ${60 - (verdict.time_left_s || 0)}s · score ${verdict.score}`
      : `Just blew today's WasntMe case. Think you can do better?`;
    const url = `${window.location.origin}/challenge?from=verdict`;
    try {
      if (navigator.share) {
        await navigator.share({ title, text: title, url });
      } else {
        await navigator.clipboard.writeText(`${title}\n${url}`);
        toast.success("Copied — paste anywhere.");
      }
    } catch {}
  };

  const copyCaption = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Caption copied.");
    } catch {
      toast.error("Copy blocked by this browser.");
    }
  };

  return (
    <div className="grain min-h-screen bg-[#0a0908] text-[#f4f0ec]">
      {/* Header strip */}
      <div className="border-b border-[#2a2525] px-5 py-4 flex items-center justify-between">
        <Link to="/" className="text-[10px] tracking-[0.3em] text-[#d4af37]" data-testid="challenge-logo">
          ◉ WASNTME.PARTY
        </Link>
        <div className="text-[9px] tracking-[0.3em] text-[#5a5050]">60-SECOND CHALLENGE</div>
      </div>

      <div className="max-w-xl mx-auto px-5 py-10">
        {/* Inbound share-link preamble */}
        {stage === "inbound" && inboundCard && (
          <div className="mb-10" data-testid="challenge-inbound">
            <div className="text-[10px] tracking-[0.3em] text-[#8b0000] mb-3">◉ YOU WERE CHALLENGED</div>
            <h1 className="font-display text-3xl mb-4 leading-tight">
              {inboundCard.correct
                ? <>Detective #{inboundCard.detective_number || "???"} <span className="text-[#d4af37]">cracked</span> today's case in {60 - (inboundCard.time_left_s || 0)}s.</>
                : <>Detective #{inboundCard.detective_number || "???"} blew today's case. <span className="text-[#8b0000]">Can you do better?</span></>}
            </h1>
            <div className="rounded-md overflow-hidden border border-[#2a2525] mb-5 max-w-sm">
              <img src={inboundCard.share_card_url} alt="Challenger's verdict card"
                className="w-full h-auto" data-testid="challenge-inbound-img" loading="lazy" />
            </div>
            <button onClick={() => setStage("intro")} className="btn-primary" data-testid="challenge-inbound-accept">
              <Flame size={13} /> I ACCEPT — RUN THE CASE
            </button>
          </div>
        )}

        {/* Intro — pre-play */}
        {stage === "intro" && (
          <div data-testid="challenge-intro">
            {fromTag === "verdict" && (
              <div className="text-[10px] tracking-[0.3em] text-[#8b0000] mb-3">
                ◉ YOU WERE CHALLENGED
              </div>
            )}
            <h1 className="font-display text-4xl sm:text-5xl text-[#f4f0ec] mb-3 leading-[1.05]">
              60 seconds.<br/>
              Three suspects.<br/>
              <span className="text-[#d4af37]">One liar.</span>
            </h1>
            <p className="text-sm text-[#a39797] mb-7 leading-relaxed max-w-md">
              Free. No signup. Pick who did it before the clock hits zero. You'll get a score, the reveal, and a shareable verdict card.
            </p>
            {caseData ? (
              <>
                <div className="card-noir p-4 mb-6">
                  <div className="text-[10px] tracking-[0.3em] text-[#d4af37] mb-2">TODAY'S CASE</div>
                  <div className="font-display text-xl text-[#f4f0ec] mb-1">{caseData.title}</div>
                  <div className="text-xs text-[#a39797] leading-relaxed">{caseData.setting}</div>
                </div>
                <button onClick={start} className="btn-primary" data-testid="challenge-start">
                  <Flame size={13} /> START THE CLOCK
                </button>
              </>
            ) : (
              <div className="flex items-center gap-2 text-sm text-[#a39797]">
                <Loader2 size={14} className="animate-spin" /> Loading today's file…
              </div>
            )}
          </div>
        )}

        {/* Playing */}
        {stage === "playing" && caseData && (
          <div data-testid="challenge-playing">
            <div className="flex items-center justify-between mb-4">
              <div className="text-[10px] tracking-[0.3em] text-[#a39797]">CASE #{caseData.id.replace("_", " ").toUpperCase()}</div>
              <div className="flex items-center gap-1.5 text-[#d4af37]" data-testid="challenge-clock">
                <Clock size={13} />
                <span className="font-display text-2xl tracking-wider">00:{String(left).padStart(2, "0")}</span>
              </div>
            </div>
            <h2 className="font-display text-2xl mb-1 leading-tight">{caseData.title}</h2>
            <p className="text-xs text-[#a39797] mb-5 leading-relaxed">{caseData.setting}</p>

            <div className="card-noir p-4 mb-5" data-testid="challenge-clues">
              <div className="text-[9px] tracking-[0.3em] text-[#d4af37] mb-2">THREE CLUES</div>
              <ol className="space-y-1.5 text-xs text-[#d4d0cc] leading-relaxed list-decimal list-inside">
                {caseData.clues.map((c, i) => <li key={i}>{c}</li>)}
              </ol>
            </div>

            <div className="text-[9px] tracking-[0.3em] text-[#d4af37] mb-2">WHO DID IT?</div>
            <div className="space-y-2 mb-5">
              {caseData.suspects.map((s) => (
                <button
                  key={s.key}
                  onClick={() => setPicked(s.key)}
                  className={`w-full text-left rounded-md border px-3 py-3 transition-colors ${
                    picked === s.key
                      ? "border-[#d4af37] bg-[#d4af37]/5"
                      : "border-[#2a2525] bg-[#1a1515] hover:border-[#8b0000]/60"
                  }`}
                  data-testid={`challenge-suspect-${s.key}`}
                >
                  <div className="text-sm">{picked === s.key ? "✓ " : ""}{s.name}</div>
                  <div className="text-[11px] text-[#a39797] mt-0.5">{s.blurb}</div>
                </button>
              ))}
            </div>

            <button
              onClick={() => submit()}
              disabled={!picked || submitting}
              className="btn-primary w-full py-3"
              data-testid="challenge-accuse"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <AlertTriangle size={14} />}
              {submitting ? "Filing the report…" : "LOCK IN MY ACCUSATION"}
            </button>
          </div>
        )}

        {/* Verdict */}
        {stage === "verdict" && verdict && (
          <div data-testid="challenge-verdict">
            <div className="text-center mb-7">
              {verdict.correct ? (
                <>
                  <CheckCircle2 size={56} className="text-[#d4af37] mx-auto mb-3" />
                  <div className="font-display text-4xl mb-1">You cracked it.</div>
                  <div className="text-sm text-[#d4af37]">
                    Score: {verdict.score} {verdict.time_bonus > 0 ? ` · +${verdict.time_bonus} time bonus` : ""}
                  </div>
                </>
              ) : (
                <>
                  <AlertTriangle size={56} className="text-[#8b0000] mx-auto mb-3" />
                  <div className="font-display text-4xl mb-1">Wrong call.</div>
                  <div className="text-sm text-[#a39797]">The real one was <span className="text-[#d4af37]">{verdict.killer_name}</span>.</div>
                </>
              )}
              {verdict.detective_number && (
                <div className="text-[10px] text-[#5a5050] mt-2">
                  YOU ARE DETECTIVE #{verdict.detective_number}
                </div>
              )}
            </div>

            <div className="card-noir p-4 mb-5">
              <div className="text-[10px] tracking-[0.3em] text-[#d4af37] mb-2">THE REVEAL</div>
              <div className="text-sm text-[#d4d0cc] leading-relaxed">{verdict.reveal}</div>
            </div>

            {verdict.share_card_url && (
              <div className="card-noir p-4 mb-5" data-testid="challenge-share-card">
                <div className="flex items-center gap-2 mb-3">
                  <Share2 size={13} className="text-[#d4af37]" />
                  <div className="text-[10px] tracking-[0.3em] text-[#d4af37]">YOUR SHAREABLE CARD</div>
                </div>
                <div className="rounded-md overflow-hidden border border-[#2a2525] mb-3 max-w-xs mx-auto bg-[#1a1515]">
                  <img src={verdict.share_card_url} alt="Your verdict card"
                    className="w-full h-auto" data-testid="challenge-share-img" loading="lazy" />
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={shareNative} className="btn-primary text-xs" data-testid="challenge-share-native">
                    <Share2 size={12} /> Share my verdict
                  </button>
                  <button onClick={() => copyCaption(verdict.share_card_url)}
                    className="btn-ghost text-xs" data-testid="challenge-share-copy-url">
                    <Copy size={12} /> Copy image URL
                  </button>
                </div>
              </div>
            )}

            {/* Platform-specific captions */}
            {verdict.captions && (
              <div className="card-noir p-4 mb-6" data-testid="challenge-captions">
                <div className="text-[10px] tracking-[0.3em] text-[#d4af37] mb-3">COPY-PASTE CAPTIONS</div>
                <div className="space-y-2">
                  {PLATFORM_META.filter(p => verdict.captions[p.key]).map(({ key, label, Icon }) => (
                    <div key={key} className="rounded-md border border-[#2a2525] bg-[#1a1515] p-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-1.5 text-xs text-[#d4af37]">
                          <Icon size={12} /> {label}
                        </div>
                        <button onClick={() => copyCaption(verdict.captions[key])}
                          className="text-[10px] text-[#a39797] hover:text-[#d4af37]"
                          data-testid={`challenge-caption-copy-${key}`}>
                          <Copy size={11} className="inline mr-1" /> Copy
                        </button>
                      </div>
                      <div className="text-[11px] text-[#d4d0cc] leading-relaxed whitespace-pre-wrap">
                        {verdict.captions[key]}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center justify-center gap-2 flex-wrap">
              <Link to="/packs" className="btn-primary text-sm" data-testid="challenge-cta-packs">
                Run a case with your crew <ArrowRight size={13} />
              </Link>
              <Link to="/" className="btn-ghost text-xs" data-testid="challenge-home">
                Back to the arena
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Challenge;
